import React, { useCallback, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, ChevronDown, ChevronUp, Copy, Check, Bug } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { DjayNarrator } from '../../mascot';
import { BaseErrorBoundary, ErrorFallbackProps } from './BaseErrorBoundary';
interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
  onNavigateHome?: () => void;
}
/** Generates a compact error code from the error for easy sharing */
function generateErrorCode(error: Error, errorInfo: React.ErrorInfo | null): string {
  const timestamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
  const msgHash = error.message.split('').reduce((h, c) => (h << 5) - h + c.charCodeAt(0) | 0, 0).toString(36).replace('-', 'n').slice(0, 6);
  const componentMatch = errorInfo?.componentStack?.match(/\n\s+at (\w+)/);
  const crashComponent = componentMatch?.[1] || 'Unknown';
  return `DJ-${timestamp}-${msgHash}-${crashComponent}`;
}
function ErrorDetails({
  error,
  errorInfo
}: {
  error: Error;
  errorInfo: React.ErrorInfo | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const errorCode = generateErrorCode(error, errorInfo);
  const fullErrorText = [`Error Code: ${errorCode}`, `Message: ${error.message}`, error.stack ? `\nStack:\n${error.stack}` : '', errorInfo?.componentStack ? `\nComponent Stack:${errorInfo.componentStack}` : ''].filter(Boolean).join('\n');
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullErrorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = fullErrorText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [fullErrorText]);
  return <div className="w-full mt-2 mb-6" data-id="element-2410" data-mp="255vi05ra1a3h">
      {/* Error code badge — always visible */}
      <div className="flex items-center justify-center gap-2 mb-3 flex-wrap" data-id="element-2411" data-mp="2k2cqmp830amc">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/70 border border-orange-200 rounded-full text-xs font-mono text-slate-600 select-all" data-id="element-2412" data-mp="2f15k0thodhh9">
          <Bug className="w-3 h-3" data-id="element-2413" data-mp="2o36fbktcltga" />
          {errorCode}
        </span>
        <button onClick={handleCopy} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-white/70 transition-colors" title="Kopieer foutdetails" data-id="element-2414" data-mp="2o0shj47aa351">
          {copied ? <>
              <Check className="w-3 h-3 text-emerald-500" data-id="element-2415" data-mp="37qvy52jb4jdd" />
              <span className="text-emerald-600" data-id="element-2416" data-mp="37sgsos5mg2b8">Gekopieerd!</span>
            </> : <>
              <Copy className="w-3 h-3" data-id="element-2417" data-mp="3qctmbutqs48g" />
              Kopieer
            </>}
        </button>
      </div>

      {/* Expandable details */}
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-center gap-1.5 mx-auto text-xs text-slate-400 hover:text-slate-600 transition-colors py-1" data-id="element-2418" data-mp="3hf170qo0swl2">
        {isOpen ? <ChevronUp className="w-3.5 h-3.5" data-id="element-2419" data-mp="37mvijqwz8xam" /> : <ChevronDown className="w-3.5 h-3.5" data-id="element-2420" data-mp="3cfoo2h09s9nr" />}
        {isOpen ? 'Verberg details' : 'Toon foutdetails'}
      </button>

      <AnimatePresence data-id="element-2421" data-mp="2yp1l2cnh7xlv">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-2422" data-mp="32pbtdvyqr03w">
            <div className="mt-3 p-4 bg-slate-900 rounded-xl text-left overflow-auto max-h-48" data-id="element-2423" data-mp="3s1rlmaplgdo2">
              <p className="text-[11px] font-mono text-rose-300 break-all leading-relaxed whitespace-pre-wrap" data-id="element-2424" data-mp="3n8go506o998t">
                {error.message}
              </p>
              {error.stack && <p className="text-[10px] font-mono text-slate-500 break-all mt-2 leading-relaxed whitespace-pre-wrap" data-id="element-2425" data-mp="3dcn5cz2d0jil">
                  {error.stack.split('\n').slice(1, 6).join('\n')}
                </p>}
              {errorInfo?.componentStack && <p className="text-[10px] font-mono text-slate-600 break-all mt-2 leading-relaxed whitespace-pre-wrap" data-id="element-2426" data-mp="2zc2ldzkpkhni">
                  {errorInfo.componentStack.split('\n').slice(0, 5).join('\n')}
                </p>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
/**
 * GlobalErrorBoundary — Catches rendering errors across the entire app shell.
 * Djay-styled fallback for a friendly, on-brand error experience.
 * Always shows a copyable error code badge for easy bug reporting.
 */
export class GlobalErrorBoundary extends Component<GlobalErrorBoundaryProps> {
  handleGoHome = (): void => {
    this.props.onNavigateHome?.();
  };
  renderFallback = ({
    error,
    errorInfo,
    onReset
  }: ErrorFallbackProps): React.ReactNode => {
    return <div className="max-w-md mx-auto mt-12 px-4" data-id="element-2427" data-mp="r3e727lc0cn0">
        <Card className="p-8 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-xl rounded-[2rem] relative overflow-hidden" data-id="element-2428" data-mp="r6a6y1gqpbe3">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-2429" data-mp="106fce62dhzpm" />

          <div className="relative z-10 flex flex-col items-center" data-id="element-2430" data-mp="27ks4odhwbnbd">
            <div className="mb-4" data-id="element-2431" data-mp="2big5fx69sab6">
              <DjayNarrator size="lg" emotion="thinking" animate data-id="element-2432" data-mp="cf9b5eaogied" />
            </div>

            <h2 className="text-2xl sm:text-3xl font-black text-slate-800 mb-2 tracking-tight" data-id="element-2433" data-mp="chdladz66dn3">
              Oeps, dat ging mis!
            </h2>
            <p className="text-slate-600 mb-2 text-base leading-relaxed font-medium max-w-sm" data-id="element-2434" data-mp="lg7hi38l9204">
              Geen zorgen — ga terug naar het dashboard en probeer het opnieuw.
              🚀
            </p>

            {error && <ErrorDetails error={error} errorInfo={errorInfo} data-id="element-2435" data-mp="3oxhaweclavn8" />}

            <Button onClick={() => {
            onReset();
            this.handleGoHome();
          }} variant="primary" size="lg" className="shadow-lg rounded-2xl hover:shadow-xl transition-shadow w-full sm:w-auto" data-id="element-2436" data-mp="3kvm9zaykmk9z">
              <Home className="w-5 h-5 mr-2" data-id="element-2437" data-mp="365of6c64ywbt" />
              Naar dashboard
            </Button>
          </div>
        </Card>
      </div>;
  };
  render(): React.ReactNode {
    return <BaseErrorBoundary logPrefix="Global" fallback={this.renderFallback} data-id="element-2438" data-mp="1gpb0m564a957">
        {this.props.children}
      </BaseErrorBoundary>;
  }
}